import React, { useState } from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Link as MuiLink, 
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
} from '@mui/material';
import { 
  Facebook, 
  Twitter, 
  Instagram, 
  YouTube,
  Language,
  Business,
  Link as LinkIcon,
  Email,
  Phone,
  LocationOn,
  Info,
  Restaurant,
  Groups,
  VolunteerActivism,
  Work,
  ContactMail,
  Close
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { keyframes } from '@emotion/react';

// Custom keyframe animation
const floatAnimation = keyframes`
  0% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
  100% { transform: translateY(0px); }
`;

const Footer = () => {
  const [openDialog, setOpenDialog] = useState(false);
  const [hoveredIcon, setHoveredIcon] = useState(null);

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const socialIcons = [
    { icon: <Facebook fontSize="medium" />, color: '#4267B2', name: 'Facebook' },
    { icon: <Twitter fontSize="medium" />, color: '#1DA1F2', name: 'Twitter' },
    { icon: <Instagram fontSize="medium" />, color: '#E1306C', name: 'Instagram' },
    { icon: <YouTube fontSize="medium" />, color: '#FF0000', name: 'YouTube' }
  ];

  const quickLinks = [
    { icon: <Info />, text: 'About Us', href: '/about' },
    { icon: <Restaurant />, text: 'Services', href: '/services' },
    { icon: <Groups />, text: 'Our Clients', href: '/clients' },
    { icon: <VolunteerActivism />, text: 'CSR', href: '/csr' },
    { icon: <Work />, text: 'Careers', href: '/careers' },
    { icon: <ContactMail />, text: 'Contact Us', href: '/contact' },
  ];
  const infoLinks = [
    { icon: <Info />, text: 'Privacy Policy', href: '/privacy-policy' },
    { icon: <Info />, text: 'Terms & Conditions', href: '/terms-and-conditions' },
    { icon: <Info />, text: 'Shipping & Delivery', href: '/shipping-and-delivery' },
    { icon: <Info />, text: 'Cancellation & Refund', href: '/cancellation-and-refund' },
  ];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.3
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <Box 
      component="footer" 
      sx={{ 
        backgroundColor: '#121212', 
        color: 'white', 
        py: 6,
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Animated background elements */}
      <Box 
        component={motion.div}
        initial={{ x: -100, y: -100, rotate: 45 }}
        animate={{ 
          x: [null, 100, -50, 0],
          y: [null, 50, -30, 0],
          rotate: [45, 90, 135, 180],
          transition: { 
            duration: 20,
            repeat: Infinity,
            repeatType: "reverse"
          }
        }}
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: 100,
          height: 100,
          border: '1px solid rgba(255, 160, 0, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />
      
      <Box 
        component={motion.div}
        initial={{ x: 100, y: 100, rotate: -45 }}
        animate={{ 
          x: [null, -100, 50, 0],
          y: [null, -50, 30, 0],
          rotate: [-45, -90, -135, -180],
          transition: { 
            duration: 25,
            repeat: Infinity,
            repeatType: "reverse"
          }
        }}
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: 150,
          height: 150,
          border: '1px solid rgba(46, 125, 50, 0.1)',
          borderRadius: '50%',
          zIndex: 0
        }}
      />

      <Container maxWidth="lg">
        <Grid 
          container 
          spacing={4}
          component={motion.div}
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {/* Company Info Column */}
          <Grid item xs={12} md={4} component={motion.div} variants={itemVariants}>
  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
    <Box
      component="span"
      sx={{
        color: '#2E7D32',
        display: 'inline-block',
        animation: `${floatAnimation} 4s ease-in-out infinite`,
      }}
    >
      FLAVR
    </Box>

    <Box
      component="span"
      sx={{
        color: '#FFA000',
        display: 'inline-block',
        animation: `${floatAnimation} 4s ease-in-out infinite 0.5s`,
      }}
    >
      APOGI
    </Box>
  </Typography>

  <Typography
    variant="body2"
    paragraph
    sx={{ color: 'rgba(255,255,255,0.7)' }}
  >
    Elevating culinary experiences through innovation and tradition.
  </Typography>

  {/* Social Icons */}
  <Box sx={{ display: 'flex', gap: 2, mt: 2, mb: 3 }}>
    {socialIcons.map((social, index) => (
      <MuiLink
        key={index}
        href={`https://www.${social.name.toLowerCase()}.com/flavrapogi`}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: hoveredIcon === index ? social.color : 'inherit',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: social.color,
            transform: 'scale(1.2)',
          },
        }}
        onMouseEnter={() => setHoveredIcon(index)}
        onMouseLeave={() => setHoveredIcon(null)}
        component={motion.a}
        whileHover={{ scale: 1.2 }}
        whileTap={{ scale: 0.9 }}
      >
        {social.icon}
      </MuiLink>
    ))}
  </Box>

  {/* Policy Links Section */}
  <Box
    sx={{
      px: 2,
      borderRadius: 3,
      background: 'rgba(255,255,255,0.04)',
      border: '1px solid rgba(255,255,255,0.08)',
      backdropFilter: 'blur(8px)',
    }}
  >
    <Typography
      variant="subtitle1"
      sx={{
        mb: 1.5,
        fontWeight: 600,
        color: '#FFA000',
      }}
    >
      Legal Information
    </Typography>

    {infoLinks.map((link, index) => (
      <MuiLink
        key={index}
        href={link.href}
        component={motion.a}
        whileHover={{ x: 5 }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          mb: 1.2,
          color: 'rgba(255,255,255,0.7)',
          textDecoration: 'none',
          transition: 'all 0.3s ease',
          '&:hover': {
            color: '#fff',
            textDecoration: 'underline',
          },
        }}
      >
        <Box
          sx={{
            color: '#FFA000',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          {link.icon}
        </Box>

        <Typography variant="body2">
          {link.text}
        </Typography>
      </MuiLink>
    ))}
  </Box>
</Grid>
          
          {/* Contact Column */}
          <Grid item xs={12} md={4} component={motion.div} variants={itemVariants}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, display: 'flex', alignItems: 'center' }}>
              <Business sx={{ 
                mr: 1,
                animation: `${floatAnimation} 3s ease-in-out infinite`
              }} /> 
              Contact
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <LocationOn sx={{ 
                mr: 2, 
                color: 'rgba(255,255,255,0.7)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#FFA000'
                }
              }} />
             <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
  Plot No-77/958, “Manasi”, “Madhuban”, Nijigarh-Kuruki<br />
  (Near Aditya Birla Public School Square),<br />
  P O- Harirajpur, Ps- Pipili,<br />
  Dist- Puri, Odisha, Pin-752050
</Typography>

            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Phone sx={{ 
                mr: 2, 
                color: 'rgba(255,255,255,0.7)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#2E7D32'
                }
              }} />
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                +91 91244 78555<br />
                96927 83839, 90000 30382
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Email sx={{ 
                mr: 2, 
                color: 'rgba(255,255,255,0.7)',
                transition: 'transform 0.3s ease',
                '&:hover': {
                  transform: 'scale(1.2)',
                  color: '#1DA1F2'
                }
              }} />
              <MuiLink 
                href="mailto:info@flavrapogi.com" 
                sx={{ 
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                info@flavrapogi.com
              </MuiLink>
            </Box>
            <Box sx={{ mt: 3 }}>
              <MuiLink 
                href="https://flavrapogi.org" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                <Language sx={{ 
                  mr: 1,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'rotate(15deg)'
                  }
                }} />
                Official Website
              </MuiLink>
              <MuiLink 
                href="https://com.flavrapogi.org" 
                target="_blank"
                rel="noopener noreferrer"
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                <LinkIcon sx={{ 
                  mr: 1,
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'rotate(15deg)'
                  }
                }} />
                Installation Portal
              </MuiLink>
            </Box>
          </Grid>
          
          {/* Quick Links Column */}
          <Grid item xs={12} md={4} component={motion.div} variants={itemVariants}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 700 }}>
              Quick Links
            </Typography>
            {quickLinks.map((link, index) => (
              <MuiLink 
                key={index}
                href={link.href} 
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  mb: 1.5,
                  color: 'rgba(255,255,255,0.7)',
                  textDecoration: 'none',
                  transition: 'color 0.3s ease',
                  '&:hover': { 
                    color: 'white',
                    textDecoration: 'underline'
                  }
                }}
                component={motion.a}
                whileHover={{ x: 5 }}
              >
                <Box 
                  component="span"
                  sx={{ 
                    mr: 1,
                    transition: 'transform 0.3s ease',
                    '&:hover': {
                      transform: 'scale(1.2)'
                    }
                  }}
                >
                  {link.icon}
                </Box>
                {link.text}
              </MuiLink>
            ))}
          </Grid>
        </Grid>
        
        <Divider sx={{ 
          my: 4, 
          backgroundColor: 'rgba(255,255,255,0.1)',
          borderBottomWidth: 1
        }} />
        
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: 'center',
            gap: 2
          }}
          component={motion.div}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
        >

          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.5)',
              textAlign: { xs: 'center', sm: 'left' }
            }}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
          >
            Committed to sustainable and responsible food service practices
          </Typography>
          
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'rgba(255,255,255,0.5)',
              textAlign: { xs: 'center', sm: 'right' }
            }}
            component={motion.div}
            whileHover={{ scale: 1.02 }}
          >
            © {new Date().getFullYear()} <Box component="span" sx={{ color: '#2E7D32' }}>FLAVR</Box>
            <Box component="span" sx={{ color: '#FFA000' }}>APOGI</Box>. All Rights Reserved.
          </Typography>
        </Box>
      </Container>

      {/* Developer Contact Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleDialogClose}
        maxWidth="xs"
        fullWidth
        component={motion.div}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ type: 'spring', damping: 25 }}
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box 
            component={motion.div}
            animate={{ 
              rotate: [0, 10, -10, 0],
              transition: { duration: 0.5 }
            }}
          >
            Developer Contact
          </Box>
          <IconButton 
            onClick={handleDialogClose}
            component={motion.button}
            whileHover={{ rotate: 90, scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box 
            sx={{ display: 'flex', alignItems: 'center', mb: 2 }}
            component={motion.div}
            whileHover={{ x: 5 }}
          >
            <Phone sx={{ mr: 2, color: 'text.secondary' }} />
            <MuiLink 
              href="tel:+917854998757" 
              sx={{ textDecoration: 'none' }}
              component={motion.a}
              whileHover={{ scale: 1.02 }}
            >
              +91 78549 98757
            </MuiLink>
          </Box>
          <Box 
            sx={{ display: 'flex', alignItems: 'center' }}
            component={motion.div}
            whileHover={{ x: 5 }}
          >
            <Email sx={{ mr: 2, color: 'text.secondary' }} />
            <MuiLink 
              href="mailto:amreshbhuyanone@gmail.com" 
              sx={{ textDecoration: 'none' }}
              component={motion.a}
              whileHover={{ scale: 1.02 }}
            >
              amreshbhuyanone@gmail.com
            </MuiLink>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDialogClose} 
            color="primary"
            component={motion.button}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Footer;